import { useBlocker } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { Button, Modal, ReadOnlyBanner, UnsavedChangesGuard } from '@bb/ui';

/** Statuses of objects that run: they are edited through a draft (campaigns, Canvas). */
export const RUNNING_STATUSES = new Set(['active', 'scheduled']);

interface Props {
  /** A real change exists (`useEditorState().isDirty`). */
  dirty: boolean;
  /** Leaving is fine right now even when dirty (e.g. right after a save that navigates). */
  allowLeave?: () => boolean;
  /** Saves; resolves when done. Offered in the leave dialog when present. */
  onSave?: () => Promise<unknown>;
  canSave?: boolean;
  /** The object's status: a running one opens read-only with «Create draft». */
  status?: string;
  objectLabel?: string;
  onCreateDraft?: () => void;
  children: ReactNode;
}

/**
 * Frame of every editor page (BB-20): leaving with real changes asks Discard / Keep editing / Save (the browser's
 * own prompt on reload or close); without changes nothing asks. A running object is shown read-only with a banner and
 * «Create draft».
 */
export function EditorShell({ dirty, allowLeave, onSave, canSave = true, status, objectLabel = 'object', onCreateDraft, children }: Props) {
  const blocker = useBlocker({ shouldBlockFn: () => dirty && !(allowLeave?.() ?? false), withResolver: true, enableBeforeUnload: false });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const running = status !== undefined && RUNNING_STATUSES.has(status);
  const saveAndLeave = async () => {
    if (!onSave) return;
    setSaving(true);
    setError(null);
    try {
      await onSave();
      blocker.proceed?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The changes could not be saved.');
    } finally {
      setSaving(false);
    }
  };
  return (
    <>
      <UnsavedChangesGuard isDirty={dirty} />
      {running && (
        <ReadOnlyBanner>
          This {objectLabel} is running, so it opens read-only. Changes go through a draft.{' '}
          {onCreateDraft && (
            <Button size="sm" onClick={onCreateDraft}>
              Create draft
            </Button>
          )}
        </ReadOnlyBanner>
      )}
      <fieldset className="editor-shell" disabled={running} aria-label={running ? `${objectLabel} (read-only)` : undefined}>
        {children}
      </fieldset>
      <Modal
        open={blocker.status === 'blocked'}
        onOpenChange={(open) => !open && blocker.reset?.()}
        title="Unsaved changes"
        description="You changed this page and did not save. Leave without the changes?"
        footer={
          <>
            <Button variant="ghost" onClick={() => blocker.reset?.()}>
              Keep editing
            </Button>
            <Button variant="danger" onClick={() => blocker.proceed?.()}>
              Discard
            </Button>
            {onSave && (
              <Button onClick={() => void saveAndLeave()} disabled={!canSave || saving} loading={saving}>
                Save
              </Button>
            )}
          </>
        }
      >
        {error && <p role="alert">{error}</p>}
      </Modal>
    </>
  );
}
